<template>
  <SplitViewTemplate
    :screenConfig="data"
    :backButtonRoute="'/settings/intake'"
    :dataModel="formModel"
    :formConfig="setFormConfig"
    :setLoading="setLoading"
    :validationSummary="validationSummary"
    :ref="'charge-code'"
    :defaultUserSettings="false"
  >
    <template #panelNotice>
      <div
        class="split-view-panel-notice split-view-panel-notice--amber"
        v-if="riskNoticeText"
      >
        <div class="split-view-panel-notice-title">
          <svg
            class="split-view-panel-notice-icon"
            viewBox="0 0 24 24"
            stroke-width="2"
            fill="none"
            stroke-linecap="round"
            stroke-linejoin="round"
          ><path d="M12 9v4M12 17h.01M10.29 3.86 1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" /></svg>
          Historical grower-payment risk
        </div>
        {{ riskNoticeText }}
      </div>
    </template>
  </SplitViewTemplate>
</template>
<script>
import { FeatureStorage } from "@/_services/storage";
import ApiService from "@/_services/api/api.service";
import SplitViewTemplate from "../../../templates/split-view";
import RouteMixin from "@/_mixins/route.mixin";
import ColumnWidth from "@/_helpers/data-grid/ColumnWidth";

// Real meaning of S/D is unconfirmed (technical-design.md §11, FLAG-01) -
// unlike Sample Code's F/G/M, no friendly label is invented here. Both the
// grid cell and the filter tag box show the raw code.
const TYPE_FILTER_OPTIONS = [
  { key: "S", value: "S" },
  { key: "D", value: "D" },
];

export default {
  mixins: [RouteMixin],
  components: {
    SplitViewTemplate,
  },
  data() {
    return {
      setLoading: false,
      validationSummary: [],
      // BR-09/AD-05: Type/Rate/GSTIncluded/GLAccount as originally loaded,
      // captured in recordPostProcess - compared against the save payload in
      // overWritePayload to decide whether an Edit needs the blocking
      // confirmation. Undefined on Create (always risk-relevant regardless
      // of any snapshot).
      originalRiskSnapshot: undefined,
      formModel: {
        RecordId: undefined,
        Code: null,
        Description: null,
        Type: null,
        Rate: null,
        GSTIncluded: null,
        GLAccount: null,
        Confirmed: false,
        ChangedBy: "",
        CreatedBy: "",
        DateChanged: "",
        DateCreated: "",
      },
      data: {
        title: "Charge Code",
        pageRoute: "/charge-code",
        saveEndPoint: "Save_AlmondcoChargeCode",
        recordEndPoint: "AlmondcoChargeCode",
        exportFileName: "Charge Code List",
        recordPostProcess: this.recordPostProcess,
        gridData: {
          gridOptions: {
            dataSource: {
              endpoint: "AlmondcoChargeCode",
              filter: "",
              serverPaging: true,
              allowGrouping: false,
              sort: [{ selector: "Code", desc: false }],
            },
            rowSelect: {
              view: this,
              page: "charge-code",
              mode: "review",
              idProperty: "RecordId",
              idMode: "Guid",
            },
          },

          toolbar: {
            search: true,
            columnChooser: true,
            export: false,
            openFilter: true,
            openFilterByDefault: true,
            expand: false,
            customButtons: [
              {
                icon: "plus",
                label: "Add New",
                route: "/charge-code/create",
                clickAction: () => {
                  this.goToCreateChargeCode();
                },
                buttonLocation: "left",
                visible: FeatureStorage.checkPermission(
                  "AlmondcoChargeCode",
                  "create",
                ),
              },
            ],
          },
          columns: [
            {
              screenField: "Code",
              dataField: "Description",
              label: "Charge Code",
              allowHiding: false,
              sortIndex: 0,
              sortOrder: "asc",
              cellTemplate: "Record",
              cellTemplateRecord: {
                primary: [
                  {
                    dataField: "Description",
                  },
                ],
                secondary: [
                  {
                    dataField: "Code",
                  },
                ],
              },
            },
            {
              screenField: "Code",
              dataField: "Code",
              label: "Code",
              visible: false,
              showInColumnChooser: true,
            },
            {
              screenField: "Description",
              dataField: "Description",
              label: "Description",
              visible: false,
              showInColumnChooser: true,
            },
            {
              screenField: "Type",
              dataField: "Type",
              label: "Type",
              width: 125,
              minWidth: 125,
            },
            {
              screenField: "ColumnValue",
              dataField: "Type",
              filterDataField: "Type",
              filterLabel: "Type",
              filterIndex: 0,
              type: "String",
              dataSourceConfig: {
                data: TYPE_FILTER_OPTIONS,
              },
              valueExpr: "key",
              displayExpr: "value",
              visible: false,
              showInColumnChooser: false,
            },
            {
              dataField: "Rate",
              label: "Rate",
              dataType: "number",
              alignment: "right",
              format: "#,##0.0000",
              width: ColumnWidth.Price4DecimalPoints.width,
              minWidth: ColumnWidth.Price4DecimalPoints.minWidth,
            },
            {
              screenField: "GLChart",
              dataField: "GLChartCodeComputedString",
              filterDataField: "GLAccount",
              label: "GL Account",
              filterLabel: "GL Account",
              filterIndex: 1,
              allowSearch: false,
              cellTemplate: "Record",
              cellTemplateRecord: {
                secondary: [{ dataField: "GLChartCodeComputedString" }],
                primary: [{ dataField: "GLChartDescription" }],
              },
              dataSourceConfig: {
                endpoint: "api/GLChart",
                filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
                sort: { selector: "ComputedStringCode", asc: true },
              },
              valueExpr: "Code",
              displayExpr: "Description",
              searchExpr: ["ComputedStringCode", "Description"],
              width: 250,
              minWidth: 250,
              exportFields: [
                {
                  dataField: "GLChartCodeComputedString",
                  label: "GL Code",
                },
                {
                  dataField: "Description",
                  label: "GL Description",
                },
              ],
            },
          ],
          valueChangedEventListners: [],
        },
      },
    };
  },
  computed: {
    setFormConfig() {
      const form = {
        headerToolbar: {
          closeSplit: true,
          save: this.setSaveOption,
          audit: true,
          auditItems: [
            {
              itemType: "group",
              caption: "",
              items: [
                {
                  dataField: "FormattedDateCreated",
                  label: { text: "Created By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
                {
                  dataField: "FormattedDateChanged",
                  label: { text: "Changed By", location: "left" },
                  editorType: "dxTextBox",
                  editorOptions: {
                    disabled: true,
                  },
                },
              ],
            },
          ],
        },
        formItems: [
          {
            itemType: "group",
            caption: "Identification",
            colCount: 2,
            items: [
              {
                dataField: "Code",
                editorType: "dxTextBox",
                editorOptions: {
                  maxLength: 8,
                  tabIndex: 1,
                },
                required: true,
                isPrimaryKeyField: true,
                label: { text: "Code" },
                visible: true,
                allowUpdate: this.baseView.mode == "create",
              },
              {
                dataField: "Description",
                editorType: "dxTextBox",
                editorOptions: {
                  maxLength: 30,
                  tabIndex: 2,
                },
                label: { text: "Description" },
                visible: true,
              },
            ],
          },
          {
            itemType: "group",
            caption: "Classification",
            colCount: 2,
            items: [
              {
                dataField: "Type",
                editorType: "dxSelectBox",
                editorOptions: {
                  items: TYPE_FILTER_OPTIONS,
                  valueExpr: "key",
                  displayExpr: "value",
                  searchEnabled: true,
                  showClearButton: true,
                  tabIndex: 3,
                },
                required: true,
                label: { text: "Type" },
                visible: true,
              },
              {
                itemType: "empty",
              },
              {
                dataField: "Rate",
                editorType: "dxNumberBox",
                editorOptions: {
                  format: "#,##0.0000",
                  tabIndex: 4,
                },
                label: { text: "Rate" },
                visible: true,
              },
              {
                dataField: "GSTIncluded",
                editorType: "dxSwitch",
                editorOptions: {
                  tabIndex: 5,
                  onInitialized: (e) => {
                    e.component.beginUpdate();
                    e.component.option(
                      "value",
                      this.formModel.GSTIncluded == "Y",
                    );
                    e.component.endUpdate();
                  },
                },
                label: { text: "GST Inc" },
                visible: true,
              },
            ],
          },
          {
            itemType: "group",
            caption: "GL Account",
            colCount: 1,
            items: [
              {
                screenField: "GLChart",
                dataField: "GLAccount",
                editorType: "dxSelectBox",
                valueExpr: "Code",
                displayExpr: (item) =>
                  item ? `${item.ComputedStringCode} - ${item.Description}` : "",
                editorOptions: {
                  dataSource: ApiService.createDataSource("api/GLChart", {
                    filter: [["Inactive", "=", "N"], "and", ["Status", "=", "A"]],
                    sort: { selector: "ComputedStringCode", asc: true },
                  }),
                  searchEnabled: true,
                  searchExpr: ["ComputedStringCode", "Description"],
                  showClearButton: true,
                  placeholder: "— None —",
                  tabIndex: 6,
                },
                required: true,
                label: { text: "GL Account" },
                visible: true,
              },
            ],
          },
        ],
        overWritePayload: this.overWritePayload,
      };

      return form;
    },
    setSaveOption() {
      return FeatureStorage.checkPermission(
        "AlmondcoChargeCode",
        this.baseView.mode == "create" ? "create" : "edit",
      );
    },
    // Verbatim from prototype.html's own riskNoticeText for both modes -
    // Edit names the specific risk-relevant fields (Type/Rate/GSTIncluded/
    // GLAccount) and explicitly excludes Description, matching Sample Code
    // Master's own Edit notice shape. Undefined (hides the notice entirely,
    // see the panelNotice slot's own v-if) on review and the plain list.
    riskNoticeText() {
      if (this.baseView.mode === "create") {
        return "A newly-created Charge Code can be picked up by a grower payment recalculation covering a period before it existed, changing that period's result — this table has no season/date scoping to prevent it.";
      }
      if (this.baseView.mode === "edit" || this.baseView.mode === "review") {
        return "Grower payments already calculated using this Charge Code's Type, Rate, GST Inc, or GL Account cannot be recreated if those values change — this table has no season/date scoping. Changing Description alone does not carry this risk.";
      }
      return undefined;
    },
  },
  methods: {
    confirmRiskChange(message, title) {
      return this.$confirm({
        title,
        messageHtml: message,
        showTitle: true,
        width: 500,
        buttons: [
          { text: "Yes", onClick: () => true },
          { text: "No", onClick: () => false },
        ],
      });
    },
    goToCreateChargeCode() {
      this.$router.push({
        path: `/charge-code/create`,
      });
    },
    recordPostProcess(data) {
      // BR-09/AD-05: snapshot the risk-relevant fields exactly as loaded,
      // before any of this method's own create-mode defaults below are
      // applied - only meaningful for an existing record (edit/review);
      // stays undefined on Create, where every save is unconditionally
      // risk-relevant regardless of any snapshot.
      if (this.baseView.mode === "edit" || this.baseView.mode === "review") {
        this.originalRiskSnapshot = {
          Type: data.Type,
          Rate: data.Rate,
          GSTIncluded: data.GSTIncluded,
          GLAccount: data.GLAccount,
        };
      }
      if (data.Rate === undefined || data.Rate === null) {
        data.Rate = 0;
      }
      if (data.GSTIncluded === undefined || data.GSTIncluded === null) {
        data.GSTIncluded = "N";
      }
      return data;
    },
    async overWritePayload(payload) {
      payload.GSTIncluded =
        payload.GSTIncluded === true || payload.GSTIncluded === "Y" ? "Y" : "N";

      if (this.baseView.mode === "create") {
        const message = `Charge Code <b>${payload.Code}</b> will be available to every payment calculation from now on, including a recalculation of a <b>historical</b> period that never had this code available — this table has no season/date scoping to prevent that. This cannot be undone once saved.<br><br>Are you sure you want to continue?`;

        const confirmed = await this.confirmRiskChange(
          message,
          "Confirm new Charge Code",
        );
        if (!confirmed) {
          this.$notify("Change not saved.", "info");
          return null;
        }
        payload.Confirmed = true;
        return payload;
      }

      // BR-09/AD-05: fires only when Type/Rate/GSTIncluded/GLAccount actually
      // changed from what was loaded - a Description-only edit skips
      // confirmation entirely. Matches Sample Code Master's own Edit modal
      // shape (dynamically lists which fields changed).
      const riskFieldLabels = {
        Type: "Type",
        Rate: "Rate",
        GSTIncluded: "GST Inc",
        GLAccount: "GL Account",
      };
      const changedFields = Object.keys(riskFieldLabels).filter(
        (field) =>
          this.originalRiskSnapshot &&
          payload[field] !== this.originalRiskSnapshot[field],
      );

      if (changedFields.length > 0) {
        const message = `Changing <b>${changedFields
          .map((field) => riskFieldLabels[field])
          .join(", ")}</b> for Charge Code <b>${payload.Code}</b> means any grower payment already calculated using the old value can no longer be accurately recreated — this table has no season/date scoping to preserve it. This cannot be undone once saved.<br><br>Are you sure you want to continue?`;

        const confirmed = await this.confirmRiskChange(
          message,
          "Confirm change to an existing Charge Code",
        );
        if (!confirmed) {
          this.$notify("Change not saved.", "info");
          return null;
        }
        payload.Confirmed = true;
      }

      return payload;
    },
  },
};
</script>
